import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Switch,
  Paper,
  Chip,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  AccountCircle,
  Lock,
  Notifications,
  Palette,
  CreditCard,
  Help,
  Edit,
  Email,
  Phone,
  Language,
  Security,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

// Tab Panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 0 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage = () => {
  const [selectedMenuItem, setSelectedMenuItem] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  const menuItems = [
    { icon: <AccountCircle />, label: "Profile", color: "#1976d2" },
    { icon: <Lock />, label: "Privacy & Security", color: "#d32f2f" },
    { icon: <Notifications />, label: "Notifications", color: "#f57c00" },
    { icon: <Palette />, label: "Appearance", color: "#7b1fa2" },
    { icon: <CreditCard />, label: "Billing & Plans", color: "#388e3c" },
    { icon: <Help />, label: "Help & Support", color: "#0288d1" },
  ];

  const renderContent = () => {
    switch (selectedMenuItem) {
      case 0: // Profile
        return (
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Profile Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Manage your personal information and public profile
            </Typography>

            {/* Profile Photo Section */}
            <Card
              elevation={0}
              sx={{
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  Profile Photo
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "primary.main",
                      fontSize: "2rem",
                    }}
                  >
                    J
                  </Avatar>
                  <Box>
                    <Stack direction="row" spacing={2}>
                      <Button variant="outlined" size="small">
                        Upload Photo
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        sx={{ textTransform: "none" }}
                      >
                        Remove
                      </Button>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      JPG, PNG or GIF. Max size 2MB.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Personal Information */}
            <Card
              elevation={0}
              sx={{
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  sx={{ mb: 3 }}
                >
                  Personal Information
                </Typography>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    defaultValue="Jaime Cruz"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountCircle color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Email Address"
                    defaultValue="crosvelucero@gmail.com"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    placeholder="+1 (555) 000-0000"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Bio"
                    multiline
                    rows={3}
                    placeholder="Tell us about yourself..."
                    variant="outlined"
                  />
                </Stack>
                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <Button variant="contained" disableElevation>
                    Save Changes
                  </Button>
                  <Button variant="outlined">Cancel</Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 1: // Privacy & Security
        return (
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Privacy & Security
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Manage your security settings and password
            </Typography>

            {/* Change Password */}
            <Card
              elevation={0}
              sx={{
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  sx={{ mb: 3 }}
                >
                  Change Password
                </Typography>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" disableElevation>
                    Update Password
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Two-Factor Authentication */}
            <Card
              elevation={0}
              sx={{
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Two-Factor Authentication
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add an extra layer of security to your account
                    </Typography>
                  </Box>
                  <Switch />
                </Box>
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "error.light",
                borderRadius: 2,
                bgcolor: "error.lighter",
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="error"
                  gutterBottom
                >
                  Danger Zone
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Delete Account
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Permanently delete your account and all data
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    disableElevation
                  >
                    Delete Account
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 2: // Notifications
        return (
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Manage how you receive notifications
            </Typography>

            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Stack spacing={2} divider={<Divider />}>
                  {[
                    {
                      title: "Email Notifications",
                      desc: "Receive email updates about your account",
                    },
                    {
                      title: "Push Notifications",
                      desc: "Get push notifications on your devices",
                    },
                    {
                      title: "SMS Notifications",
                      desc: "Receive text message alerts",
                    },
                    {
                      title: "Marketing Emails",
                      desc: "Receive promotional emails and offers",
                    },
                  ].map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.desc}
                        </Typography>
                      </Box>
                      <Switch defaultChecked={idx < 2} />
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        );

      case 3: // Appearance
        return (
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Appearance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Customize how the app looks for you
            </Typography>

            <Card
              elevation={0}
              sx={{
                mb: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  Theme
                </Typography>
                <Stack direction="row" spacing={2}>
                  {["Light", "Dark", "System"].map((theme) => (
                    <Paper
                      key={theme}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: "2px solid",
                        borderColor:
                          theme === "Light" ? "primary.main" : "divider",
                        borderRadius: 2,
                        cursor: "pointer",
                        minWidth: 100,
                        textAlign: "center",
                        "&:hover": {
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {theme}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Compact Mode
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reduce spacing and show more content
                    </Typography>
                  </Box>
                  <Switch />
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 4: // Billing
        return (
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Billing & Plans
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Manage your subscription and payment methods
            </Typography>

            {/* Current Plan */}
            <Card
              elevation={0}
              sx={{
                mb: 3,
                border: "2px solid",
                borderColor: "primary.main",
                borderRadius: 2,
                background:
                  "linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Chip
                      label="Current Plan"
                      color="primary"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="h6" fontWeight={600}>
                      Pro Plan
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      $29/month • Billed monthly
                    </Typography>
                  </Box>
                  <Button variant="outlined" size="small">
                    Change Plan
                  </Button>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Next billing date: March 9, 2026
                </Typography>
              </CardContent>
            </Card>

            {/* Payment Methods */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Payment Methods
                  </Typography>
                  <Button variant="outlined" size="small">
                    Add New
                  </Button>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <CreditCard color="action" />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        •••• •••• •••• 4242
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Expires 12/25
                      </Typography>
                    </Box>
                  </Box>
                  <Chip label="Default" size="small" color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 5: // Help
        return (
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Help & Support
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Get help and contact support
            </Typography>

            <Stack spacing={2}>
              {[
                {
                  title: "Documentation",
                  desc: "Browse our comprehensive guides",
                  action: "View Docs",
                },
                {
                  title: "Contact Support",
                  desc: "Get help from our support team",
                  action: "Contact",
                },
                {
                  title: "Community Forum",
                  desc: "Connect with other users",
                  action: "Visit Forum",
                },
                {
                  title: "Report a Bug",
                  desc: "Help us improve by reporting issues",
                  action: "Report",
                },
              ].map((item, idx) => (
                <Card
                  key={idx}
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.desc}
                        </Typography>
                      </Box>
                      <Button variant="outlined" size="small">
                        {item.action}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        bgcolor: "background.default",
        overflow: "hidden",
      }}
    >
      {/* Sidebar */}
      <Paper
        elevation={0}
        sx={{
          width: 280,
          flexShrink: 0,
          borderRight: "1px solid",
          borderColor: "divider",
          overflow: "auto",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your account
          </Typography>
        </Box>

        <List sx={{ px: 2, pb: 2 }}>
          {menuItems.map((item, index) => (
            <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={selectedMenuItem === index}
                onClick={() => setSelectedMenuItem(index)}
                sx={{
                  borderRadius: 2,
                  "&.Mui-selected": {
                    bgcolor: "primary.lighter",
                    color: "primary.main",
                    "& .MuiListItemIcon-root": {
                      color: "primary.main",
                    },
                    "&:hover": {
                      bgcolor: "primary.lighter",
                    },
                  },
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: "text.secondary",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: selectedMenuItem === index ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          bgcolor: "grey.50",
        }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto", p: 4 }}>{renderContent()}</Box>
      </Box>
    </Box>
  );
};

export default SettingsPage;
